import React, { useLayoutEffect, useRef, useState } from "react";

export type AnimationPhase = "none" | "scrolling" | "resizing" | "complete";

/** Match CSS transition duration for the panel resize. */
const RESIZE_DURATION_MS = 500;
/** Small delay so the re-enabled transition is picked up before the resize. */
const TRANSITION_SETTLE_MS = 50;

export interface WorkspaceEntrance {
  animationPhase: AnimationPhase;
  workspaceOpacity: number;
}

/**
 * Three-phase entrance: snap the chat to full width without a transition,
 * animate it back to its target width, then fade the workspace in.
 */
function runEntranceAnimation(
  container: HTMLDivElement,
  setAnimationPhase: (phase: AnimationPhase) => void,
  setWorkspaceOpacity: (opacity: number) => void
): void {
  // Generic form rather than an `as HTMLElement` assertion: the ESLint
  // gate autofixes that assertion away, which loses .style/.offsetHeight.
  const chatPanel = container.querySelector<HTMLElement>(".flex-none");
  if (!chatPanel) return;

  chatPanel.style.transition = "none";
  chatPanel.style.width = "100%";

  // Force a reflow to ensure the style is applied
  void chatPanel.offsetHeight;

  requestAnimationFrame(() => {
    chatPanel.style.transition = "";
    setTimeout(() => {
      setAnimationPhase("resizing");
      setTimeout(() => {
        setAnimationPhase("complete");
        setWorkspaceOpacity(1);
      }, RESIZE_DURATION_MS);
    }, TRANSITION_SETTLE_MS);
  });
}

/**
 * Orchestrates the workspace entrance animation and resets it when the
 * workspace is dismissed.
 *
 * The reset only runs once layoutMode is no longer 'workspace': when a search
 * returns 0 results the mode stays 'workspace' and the empty-state table must
 * remain visible.
 */
export function useWorkspaceEntrance(
  hasResults: boolean,
  layoutMode: string,
  splitLayoutRef: React.RefObject<HTMLDivElement | null>,
  onReset: () => void
): WorkspaceEntrance {
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>("none");
  const [workspaceOpacity, setWorkspaceOpacity] = useState<number>(0);

  // Held in a ref so a fresh inline callback does not re-run the effect
  const onResetRef = useRef(onReset);
  onResetRef.current = onReset;

  useLayoutEffect(() => {
    if (hasResults && animationPhase === "none") {
      setAnimationPhase("scrolling");
      setWorkspaceOpacity(0);
      if (splitLayoutRef.current) {
        runEntranceAnimation(splitLayoutRef.current, setAnimationPhase, setWorkspaceOpacity);
      }
    } else if (!hasResults && animationPhase !== "none" && layoutMode !== "workspace") {
      setAnimationPhase("none");
      setWorkspaceOpacity(0);
      onResetRef.current();
    }
  }, [hasResults, animationPhase, layoutMode, splitLayoutRef]);

  return { animationPhase, workspaceOpacity };
}
